export interface AdminUser {
  id: number;
  empId: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  roleId: number;
  department: string;
  status: "Active" | "Inactive" | "Suspended";
  lastLogin: string;
  createdAt: string;
  avatar: string;
  twoFA: boolean;
  loginCount: number;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  userCount: number;
  color: string;
  permissions: Record<string, string[]>;
  isSystem: boolean;
}

export interface AuditLog {
  id: number;
  userId: number;
  userName: string;
  userAvatar: string;
  action: string;
  module: string;
  target: string;
  details: string;
  ip: string;
  timestamp: string;
  severity: "info" | "warning" | "critical";
  status: "success" | "failed";
}

export const mockAdminUsers: AdminUser[] = [
  {
    id: 1, empId: "EMP-001", name: "Rajesh Kumar", email: "admin@cadsoft.in",
    phone: "+91 98001 00001", role: "Super Admin", roleId: 1,
    department: "Management", status: "Active", lastLogin: "2026-04-06T09:15:00",
    createdAt: "2020-01-01", avatar: "RK", twoFA: true, loginCount: 842,
  },
  {
    id: 2, empId: "EMP-006", name: "Priya Sharma", email: "priya@cadsoft.in",
    phone: "+91 98001 11006", role: "Survey Manager", roleId: 2,
    department: "Management", status: "Active", lastLogin: "2026-04-06T08:45:00",
    createdAt: "2020-07-01", avatar: "PS", twoFA: true, loginCount: 612,
  },
  {
    id: 3, empId: "EMP-007", name: "Amit Patel", email: "amit@cadsoft.in",
    phone: "+91 98001 11007", role: "Office Staff", roleId: 3,
    department: "Office", status: "Active", lastLogin: "2026-04-06T09:00:00",
    createdAt: "2021-09-15", avatar: "AP", twoFA: false, loginCount: 389,
  },
  {
    id: 4, empId: "EMP-002", name: "Ravi Teja", email: "ravi@cadsoft.in",
    phone: "+91 98001 11001", role: "Field Surveyor", roleId: 4,
    department: "Field Operations", status: "Active", lastLogin: "2026-04-05T17:30:00",
    createdAt: "2022-03-15", avatar: "RT", twoFA: false, loginCount: 201,
  },
  {
    id: 5, empId: "EMP-003", name: "Kiran Babu", email: "kiran@cadsoft.in",
    phone: "+91 98001 11003", role: "Field Surveyor", roleId: 4,
    department: "Field Operations", status: "Active", lastLogin: "2026-04-05T18:00:00",
    createdAt: "2021-01-10", avatar: "KB", twoFA: false, loginCount: 178,
  },
  {
    id: 6, empId: "EMP-009", name: "Lakshmi Devi", email: "lakshmi@cadsoft.in",
    phone: "+91 98001 11009", role: "GIS Analyst", roleId: 3,
    department: "Office", status: "Active", lastLogin: "2026-04-04T16:20:00",
    createdAt: "2022-11-01", avatar: "LD", twoFA: false, loginCount: 145,
  },
  {
    id: 7, empId: "EMP-010", name: "Venkat Reddy", email: "venkat.lab@cadsoft.in",
    phone: "+91 98001 11010", role: "Lab Analyst", roleId: 5,
    department: "Lab", status: "Inactive", lastLogin: "2026-03-20T11:00:00",
    createdAt: "2024-01-15", avatar: "VR", twoFA: false, loginCount: 34,
  },
  {
    id: 8, empId: "CLI-001", name: "Sunita Reddy", email: "sunita@client.com",
    phone: "+91 98002 00001", role: "Client Portal User", roleId: 6,
    department: "External", status: "Active", lastLogin: "2026-04-03T14:00:00",
    createdAt: "2025-06-01", avatar: "SR", twoFA: false, loginCount: 22,
  },
  {
    id: 9, empId: "EMP-005", name: "Mahesh Babu", email: "mahesh@cadsoft.in",
    phone: "+91 98001 11005", role: "Field Surveyor", roleId: 4,
    department: "Field Operations", status: "Suspended", lastLogin: "2026-03-15T10:00:00",
    createdAt: "2023-02-14", avatar: "MB", twoFA: false, loginCount: 89,
  },
];

export const mockRoles: Role[] = [
  {
    id: 1, name: "Super Admin", description: "Full system access — all modules, settings, user management",
    userCount: 1, color: "#ef4444", isSystem: true,
    permissions: {
      Dashboard: ["view", "edit"],
      "Job Cards": ["view", "create", "edit", "delete", "export"],
      Clients: ["view", "create", "edit", "delete", "export"],
      Billing: ["view", "create", "edit", "delete", "export"],
      Employees: ["view", "create", "edit", "delete"],
      Reports: ["view", "export"],
      Settings: ["view", "edit"],
      "Admin Panel": ["view", "edit", "delete"],
      Deliverables: ["view", "upload", "approve", "delete"],
    },
  },
  {
    id: 2, name: "Survey Manager", description: "Manages jobs, clients, billing, and team assignments",
    userCount: 1, color: "#f59e0b", isSystem: false,
    permissions: {
      Dashboard: ["view"],
      "Job Cards": ["view", "create", "edit", "export"],
      Clients: ["view", "create", "edit", "export"],
      Billing: ["view", "create", "edit", "export"],
      Employees: ["view", "edit"],
      Reports: ["view", "export"],
      Settings: ["view"],
      "Admin Panel": [],
      Deliverables: ["view", "upload", "approve"],
    },
  },
  {
    id: 3, name: "Office Staff", description: "Can view and manage job cards and clients, limited billing",
    userCount: 2, color: "#3b82f6", isSystem: false,
    permissions: {
      Dashboard: ["view"],
      "Job Cards": ["view", "create", "edit"],
      Clients: ["view", "create", "edit"],
      Billing: ["view"],
      Employees: ["view"],
      Reports: ["view"],
      Settings: [],
      "Admin Panel": [],
      Deliverables: ["view", "upload"],
    },
  },
  {
    id: 4, name: "Field Surveyor", description: "Field app access — check-in, job updates, photo uploads",
    userCount: 3, color: "#10b981", isSystem: false,
    permissions: {
      Dashboard: ["view"],
      "Job Cards": ["view", "edit"],
      Clients: ["view"],
      Billing: [],
      Employees: [],
      Reports: [],
      Settings: [],
      "Admin Panel": [],
      Deliverables: ["view", "upload"],
    },
  },
  {
    id: 5, name: "Lab Analyst", description: "Access to soil test jobs and lab reports only",
    userCount: 1, color: "#8b5cf6", isSystem: false,
    permissions: {
      Dashboard: ["view"],
      "Job Cards": ["view"],
      Clients: [],
      Billing: [],
      Employees: [],
      Reports: ["view"],
      Settings: [],
      "Admin Panel": [],
      Deliverables: ["view", "upload"],
    },
  },
  {
    id: 6, name: "Client Portal User", description: "Read-only access to client portal — job status and deliverables",
    userCount: 1, color: "#64748b", isSystem: false,
    permissions: {
      Dashboard: [],
      "Job Cards": ["view"],
      Clients: [],
      Billing: [],
      Employees: [],
      Reports: [],
      Settings: [],
      "Admin Panel": [],
      Deliverables: ["view"],
    },
  },
];

export const mockAuditLogs: AuditLog[] = [
  { id: 1, userId: 1, userName: "Rajesh Kumar", userAvatar: "RK", action: "User Created", module: "Admin Panel", target: "Venkat Reddy (EMP-010)", details: "New user account created with Lab Analyst role", ip: "192.168.1.10", timestamp: "2026-04-06T09:10:00", severity: "info", status: "success" },
  { id: 2, userId: 2, userName: "Priya Sharma", userAvatar: "PS", action: "Job Card Updated", module: "Job Cards", target: "HYD-SURV-2026-04-0034", details: "Status changed from Pending to In Progress", ip: "192.168.1.12", timestamp: "2026-04-06T09:05:00", severity: "info", status: "success" },
  { id: 3, userId: 3, userName: "Amit Patel", userAvatar: "AP", action: "Invoice Created", module: "Billing", target: "INV-2026-0092", details: "New invoice created for HMDA Projects — ₹1,25,000", ip: "192.168.1.15", timestamp: "2026-04-06T08:55:00", severity: "info", status: "success" },
  { id: 4, userId: 9, userName: "Unknown", userAvatar: "??", action: "Login Failed", module: "Auth", target: "mahesh@cadsoft.in", details: "Invalid password — account suspended after 5 attempts", ip: "103.45.67.89", timestamp: "2026-04-06T08:30:00", severity: "critical", status: "failed" },
  { id: 5, userId: 1, userName: "Rajesh Kumar", userAvatar: "RK", action: "Role Modified", module: "Admin Panel", target: "Office Staff Role", details: "Added 'export' permission to Billing module", ip: "192.168.1.10", timestamp: "2026-04-05T17:45:00", severity: "warning", status: "success" },
  { id: 6, userId: 2, userName: "Priya Sharma", userAvatar: "PS", action: "Deliverable Approved", module: "Deliverables", target: "ORR_DGPS_Survey_Report_v2.pdf", details: "File status changed from Under Review to Approved", ip: "192.168.1.12", timestamp: "2026-04-05T16:30:00", severity: "info", status: "success" },
  { id: 7, userId: 1, userName: "Rajesh Kumar", userAvatar: "RK", action: "Settings Updated", module: "Settings", target: "Company Profile", details: "GST number updated to 36AABCG1234F1Z5", ip: "192.168.1.10", timestamp: "2026-04-05T15:00:00", severity: "warning", status: "success" },
  { id: 8, userId: 3, userName: "Amit Patel", userAvatar: "AP", action: "Client Created", module: "Clients", target: "Infra Builders Ltd.", details: "New client record added to CRM", ip: "192.168.1.15", timestamp: "2026-04-05T14:20:00", severity: "info", status: "success" },
  { id: 9, userId: 4, userName: "Ravi Teja", userAvatar: "RT", action: "Field Check-In", module: "Field Tracking", target: "HYD-SURV-2026-04-0034", details: "GPS check-in at Shamshabad Industrial Zone (17.38°N, 78.48°E)", ip: "49.205.12.34", timestamp: "2026-04-05T08:15:00", severity: "info", status: "success" },
  { id: 10, userId: 1, userName: "Rajesh Kumar", userAvatar: "RK", action: "User Suspended", module: "Admin Panel", target: "Mahesh Babu (EMP-005)", details: "Account suspended due to repeated failed login attempts", ip: "192.168.1.10", timestamp: "2026-04-04T11:30:00", severity: "critical", status: "success" },
  { id: 11, userId: 2, userName: "Priya Sharma", userAvatar: "PS", action: "Quotation Approved", module: "Billing", target: "QT-2026-0045", details: "Quotation approved and converted to Job Card", ip: "192.168.1.12", timestamp: "2026-04-04T10:00:00", severity: "info", status: "success" },
  { id: 12, userId: 6, userName: "Lakshmi Devi", userAvatar: "LD", action: "File Uploaded", module: "Deliverables", target: "Shamshabad_Field_Photos.zip", details: "45.2 MB ZIP file uploaded for job HYD-SURV-2026-04-0034", ip: "192.168.1.18", timestamp: "2026-04-04T09:30:00", severity: "info", status: "success" },
  { id: 13, userId: 1, userName: "Rajesh Kumar", userAvatar: "RK", action: "Bulk Export", module: "Reports", target: "Revenue Report Q1 2026", details: "Full revenue report exported as PDF — 48 pages", ip: "192.168.1.10", timestamp: "2026-04-03T16:00:00", severity: "warning", status: "success" },
  { id: 14, userId: 8, userName: "Sunita Reddy", userAvatar: "SR", action: "Portal Login", module: "Client Portal", target: "Client Portal", details: "Client portal accessed with code HMDA2026", ip: "122.167.45.23", timestamp: "2026-04-03T14:00:00", severity: "info", status: "success" },
  { id: 15, userId: 3, userName: "Amit Patel", userAvatar: "AP", action: "Payment Recorded", module: "Billing", target: "INV-2026-0089", details: "Payment of ₹85,000 recorded via NEFT — UTR: HDFC2026040312345", ip: "192.168.1.15", timestamp: "2026-04-03T11:00:00", severity: "info", status: "success" },
];

export const mockSystemStats = {
  totalUsers: 9,
  activeUsers: 7,
  totalJobs: 248,
  totalClients: 34,
  totalInvoices: 92,
  storageUsed: "2.4 GB",
  storageTotal: "10 GB",
  storagePercent: 24,
  dbSize: "145 MB",
  lastBackup: "2026-04-06T03:00:00",
  uptime: "99.8%",
  apiCalls: 14820,
  logsCount: 1248,
};
